import { Button } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import React, { useState } from 'react';
import { Translate } from 'react-localize-redux';

import Modal from '../../../components/general/modal';
import { SnackbarTypeSeverity } from '../../../constants/types';
import { deliverMaterialRequestService } from '../../../services/request';
import { canMarkMaterialDelivered } from './requestModification';
import { IRequest, ServiceType } from './types';
import { useRequestMutation } from './useRequestMutation';

export interface MaterialDeliveredActionCoreProps {
    request: IRequest;
    requestServiceType: ServiceType;
    permissions: string[];
    loading: boolean;
    onConfirm: () => void | Promise<boolean>;
}

// Presentational core: shows the "Matériel délivré" button + a confirmation modal when the
// policy allows it (canMarkMaterialDelivered), otherwise renders nothing.
//
// SHER-908 — COMPLETED means the shoe was made; it does not mean the patient has it. The
// cordonnerie needs to record the handover, and until now the request's last state was
// "performed", so a finished pair sitting on a shelf looked identical to one collected
// weeks ago.
export const MaterialDeliveredActionCore: React.FC<MaterialDeliveredActionCoreProps> = ({ request, requestServiceType, permissions, loading, onConfirm }) => {
    const [open, setOpen] = useState(false);

    if (!canMarkMaterialDelivered(request, requestServiceType, permissions)) {
        return null;
    }

    // Modal.confirmAction does not auto-close; close only on success so a rejected delivery
    // leaves the modal open. onConfirm resolves useRequestMutation.run's boolean.
    const handleConfirm = async () => {
        if (await onConfirm()) {
            setOpen(false);
        }
    };

    return (
        <>
            <Modal
                open={open}
                closeModal={() => setOpen(false)}
                confirmAction={handleConfirm}
                disabled={loading}
                title={<Translate id="pages.hospital.services.request.deliver.title" />}
            >
                <Translate id="pages.hospital.services.request.deliver.modal" />
            </Modal>
            {/* SHER-1062: the "Prochaine action" of the request-management panel — the one
                filled button in it, because it is the single step the workflow offers here. */}
            <Button variant="contained" color="success" startIcon={<CheckIcon />} onClick={() => setOpen(true)} data-testid="deliver-request">
                <Translate id="pages.hospital.services.request.deliver.button" />
            </Button>
        </>
    );
};

interface MaterialDeliveredActionProps {
    request: IRequest;
    requestServiceType: ServiceType;
    uuidInvestigation: string;
    permissions: string[];
    onDelivered: (updated: IRequest) => void;
    setShowSnackbar: (snackbar: { show: boolean; message?: string; severity?: SnackbarTypeSeverity }) => void;
}

// Container: wires the delivery service to the presentational core.
const MaterialDeliveredAction: React.FC<MaterialDeliveredActionProps> = ({ request, requestServiceType, uuidInvestigation, permissions, onDelivered, setShowSnackbar }) => {
    const { loading, run } = useRequestMutation(setShowSnackbar, onDelivered);

    const handleConfirm = () =>
        run(() => deliverMaterialRequestService(uuidInvestigation, request.id), 'pages.hospital.services.request.deliver.success');

    return (
        <MaterialDeliveredActionCore
            request={request}
            requestServiceType={requestServiceType}
            permissions={permissions}
            loading={loading}
            onConfirm={handleConfirm}
        />
    );
};

export default MaterialDeliveredAction;
